import { debounce, removeTrapFocus, trapFocus } from "utils";

class DrawerMenu extends HTMLElement {
  #closeHandler;

  constructor() {
    super();
    this.#closeHandler = this.closeAllOpenPanels.bind(this);
  }

  connectedCallback() {
    this.modal = this.closest("modal-dialog");
    this.bannerHeightObserver = null;
    this.setCurrentPanelId();
    this.bindEvents();
    this.modal.addEventListener("on:modal:closed", this.#closeHandler);
  }

  disconnectedCallback() {
    this.modal.removeEventListener("on:modal:closed", this.#closeHandler);
  }

  setCurrentPanelId() {
    const openMenuItems = Array.from(this.querySelectorAll(".menu-item-disclosure.is-open")).pop();
    this.currentPanelId = openMenuItems ? openMenuItems.id : null;
  }

  bindEvents() {
    // Bind click events to button triggers
    this.querySelectorAll(".menu-item-disclosure > button.list-menu__link").forEach((button) => {
      button.addEventListener("click", this.onTriggerClick.bind(this));
    });

    // Bind click events to back buttons
    this.querySelectorAll("button.drawer-nav__back").forEach((button) => {
      button.addEventListener("click", this.onCloseButtonClick.bind(this));
    });
  }

  enableBannerHeightControl(banner) {
    if (!banner) {
      return;
    }

    this.bannerHeightObserver = new ResizeObserver(
      debounce((entries) => {
        entries.forEach((entry) => {
          entry.target.style.setProperty(
            "--banner-height",
            `${Math.round(entry.contentRect.height)}px`
          );
        });
      }, 10)
    );
    this.bannerHeightObserver.observe(banner);
  }

  disableBannerHeightControl() {
    if (this.bannerHeightObserver) {
      this.bannerHeightObserver.disconnect();
      this.bannerHeightObserver = null;
    }
  }

  onTriggerClick(event) {
    const trigger = event.currentTarget;
    const container = trigger.parentNode;
    const isOpen = container.classList.contains("is-open");

    if (container === this.mainDetailsToggle) {
      if (isOpen) {
        event.preventDefault();
      }
      if (isOpen) {
        this.closeMenuDrawer(event, trigger);
      } else {
        this.openMenuDrawer(trigger);
      }
    } else {
      event.preventDefault();

      if (isOpen) {
        this.closeSubmenu(container);
      } else {
        setTimeout(() => {
          this.currentPanelId = this.openPanel(container);
        }, 100);
      }
    }
  }

  onCloseButtonClick(event) {
    const container = event.currentTarget.closest(".menu-item-disclosure");
    this.closeSubmenu(container);
  }

  closeSubmenu(container, animate = true) {
    container.classList.remove("is-opening", "is-open");

    // Get trigger button
    const trigger = container.querySelector(":scope > button.list-menu__link");
    const panel = container.querySelector(":scope > .drawer-nav__panel");

    if (trigger) {
      trigger.setAttribute("aria-expanded", "false");
    }

    if (panel) {
      panel.setAttribute("hidden", "");
    }

    const parentContainer = container.parentElement.closest(".menu-item-disclosure.is-open");
    const banner = parentContainer
      ? document.getElementById(`${parentContainer.id}-banner`)
      : null;

    this.disableBannerHeightControl();

    if (banner) {
      container.closest(".modal").classList.add("has-banner");
      this.enableBannerHeightControl(banner);
    } else {
      container.closest(".modal").classList.remove("has-banner");
    }

    if (trigger) {
      removeTrapFocus(trigger);
    }

    if (animate) {
      DrawerMenu.closeAnimation(container, trigger);
    } else {
      const parentOpen = container.closest(".menu-item-disclosure.is-open");
      if (parentOpen) {
        const parentTrigger = parentOpen.querySelector(":scope > button.list-menu__link");
        if (parentTrigger) {
          trapFocus(parentOpen, parentTrigger);
        }
      }
    }

    this.currentPanelId = parentContainer ? parentContainer.id : null;
  }

  static closeAnimation(container, trigger) {
    let startTime;

    const animate = (currentTime) => {
      if (startTime === undefined) {
        startTime = currentTime;
      }

      if (currentTime - startTime < 400) {
        window.requestAnimationFrame(animate);
      } else {
        const parentOpen = container.closest(".menu-item-disclosure.is-open");
        if (parentOpen) {
          const parentTrigger = parentOpen.querySelector(":scope > button.list-menu__link");
          if (parentTrigger) {
            trapFocus(parentOpen, parentTrigger);
          }
        }
      }
    };

    window.requestAnimationFrame(animate);
  }

  openMenuPanelById(panelId, animate = true, focus = true) {
    const container = document.getElementById(panelId);
    if (!container) {
      return;
    }

    this.closeAllOpenPanels();

    const parentContainer = container.parentElement.closest(".menu-item-disclosure");
    if (parentContainer) {
      parentContainer.classList.add("is-open");
      const parentPanel = parentContainer.querySelector(":scope > .drawer-nav__panel");
      if (parentPanel) {
        parentPanel.removeAttribute("hidden");
      }

      if (animate) {
        setTimeout(() => {
          this.openPanel(parentContainer, false);
        }, 100);
      } else {
        this.openPanel(parentContainer, false);
      }
    }

    container.classList.add("is-open");
    const panel = container.querySelector(":scope > .drawer-nav__panel");
    if (panel) {
      panel.removeAttribute("hidden");
    }

    if (animate) {
      setTimeout(() => {
        this.currentPanelId = this.openPanel(container, focus);
      }, 100);
    } else {
      this.currentPanelId = this.openPanel(container, focus);
    }
  }

  openPanel(container, shouldFocus = true) {
    // Get trigger button and panel
    const trigger = container.querySelector(":scope > button.list-menu__link");
    const panel = container.querySelector(":scope > .drawer-nav__panel");
    const banner = document.getElementById(`${container.id}-banner`);
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function onTransitionEnd() {
      if (panel) {
        const backButton = panel.querySelector("button.drawer-nav__back");
        if (backButton) {
          trapFocus(panel, backButton);
        }
        panel.removeEventListener("transitionend", onTransitionEnd);
      }
    }

    this.disableBannerHeightControl();

    if (banner) {
      this.enableBannerHeightControl(banner);
      container.closest(".modal").classList.add("has-banner");
    } else {
      container.closest(".modal").classList.remove("has-banner");
    }

    container.classList.add("is-opening", "is-open");

    if (panel) {
      panel.removeAttribute("hidden");
    }

    if (trigger) {
      trigger.setAttribute("aria-expanded", "true");
    }

    if (shouldFocus && panel) {
      if (!prefersReducedMotion || prefersReducedMotion.matches) {
        onTransitionEnd();
      } else {
        panel.addEventListener("transitionend", onTransitionEnd);
      }
    }

    return container.id;
  }

  closeAllOpenPanels() {
    this.querySelectorAll(".menu-item-disclosure.is-open").forEach((container) => {
      this.closeSubmenu(container, false);
    });
  }
}

customElements.define("drawer-menu", DrawerMenu);
